import { GapApi } from '../../../environments/api-urls';

/** A user's level on GAP, as authz grants it and APIM requires it: each includes the ones before. */
export type GapLevel = 'read' | 'write' | 'admin';

const LADDER: readonly GapLevel[] = ['read', 'write', 'admin'];

/** The GAP APIs whose operations APIM gates by level: all of them but the feature-flag and authorization services. */
export type GapServiceApi = Exclude<GapApi, 'fflag' | 'authz'>;

export const GAP_SERVICE_APIS: readonly GapServiceApi[] = ['client', 'env', 'partner', 'alert', 'envserv', 'ident', 'ltp'];

export type GapHttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * An operation of a GAP API, by its HTTP method and path as the API's OpenAPI document gives it:
 * `client:POST /v1/clients`, `env:PUT /v1/environments/{id}/gains-connect`. Never the generated operation id, which
 * changes when a method of the service is renamed or another one is added before it.
 */
export type GapOperation = `${GapServiceApi}:${GapHttpMethod} /${string}`;

/**
 * The level each operation of each GAP API requires, as its service publishes it (GET /v1/operations/permissions),
 * keyed by `METHOD /path`. An API whose list could not be read is absent: its operations are not offered.
 */
export type GapOperationLevels = Partial<Record<GapServiceApi, Record<string, GapLevel>>>;

/** One entry of GET /v1/operations/permissions. */
export interface GapOperationPermission {
  Method: string;
  Path: string;
  Permission: string;
}

/** The levels of one API, as its service lists them; an entry whose level GAP does not know is left out. */
export function levelsOf(permissions: GapOperationPermission[]): Record<string, GapLevel> {
  return Object.fromEntries(
    permissions
      .filter(permission => LADDER.includes(permission.Permission as GapLevel))
      .map(permission => [`${permission.Method.toUpperCase()} ${permission.Path}`, permission.Permission as GapLevel])
  );
}

/**
 * The level APIM requires for this operation: what its service lists, admin when it lists the API but not the
 * operation (as APIM does); null when the API's list could not be read.
 */
export function requiredLevel(levels: GapOperationLevels, operation: GapOperation): GapLevel | null {
  const separator = operation.indexOf(':');
  const list = levels[operation.slice(0, separator) as GapServiceApi];
  return list ? (list[operation.slice(separator + 1)] ?? 'admin') : null;
}

/** Whether a user at this level may run an operation requiring that one. */
export function allows(level: GapLevel, required: GapLevel): boolean {
  return LADDER.indexOf(level) >= LADDER.indexOf(required);
}

/** authz's `READER`, `WRITER` or `ADMIN` as a GAP level; null for anything else. */
export function toGapLevel(accessLevel: string | null | undefined): GapLevel | null {
  switch (accessLevel?.toUpperCase()) {
    case 'READER':
      return 'read';
    case 'WRITER':
      return 'write';
    case 'ADMIN':
      return 'admin';
    default:
      return null;
  }
}
